import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import AssessmentIcon from '@mui/icons-material/Assessment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export type IconComponent = ComponentType<SvgIconProps>;

export interface MenuLeaf {
  kind: 'leaf';
  /** Chave em pt-BR.json (sob `menu.`). */
  labelKey: string;
  /** Path do React Router. Para links externos, use `externalUrl`. */
  path?: string;
  /** URL externa (abre em nova aba). Exclusivo com `path`. */
  externalUrl?: string;
}

export interface MenuGroup {
  kind: 'group';
  labelKey: string;
  icon: IconComponent;
  children: MenuLeaf[];
}

export type MenuEntry = MenuGroup | (MenuLeaf & { icon: IconComponent });

/**
 * Menu Admin completo conforme RF-34 (PRD) e mapeamento de telas/cadastros.
 * `gamDashboard` é link externo (RF-30 — segue servido pelo front GeneXus).
 */
export const ADMIN_MENU: MenuEntry[] = [
  { kind: 'leaf', labelKey: 'menu.home', path: '/', icon: HomeIcon },
  {
    kind: 'group',
    labelKey: 'menu.company',
    icon: BusinessIcon,
    children: [
      { kind: 'leaf', labelKey: 'menu.companyRegistry', path: '/empresas' },
      { kind: 'leaf', labelKey: 'menu.companyInvoices', path: '/empresas/notas-fiscais' },
      { kind: 'leaf', labelKey: 'menu.companyStatement', path: '/empresas/extrato' },
    ],
  },
  {
    kind: 'group',
    labelKey: 'menu.clients',
    icon: WorkOutlineIcon,
    children: [
      { kind: 'leaf', labelKey: 'menu.clientsRegistry', path: '/clientes' },
      { kind: 'leaf', labelKey: 'menu.clientsProjects', path: '/clientes/projetos' },
      {
        kind: 'leaf',
        labelKey: 'menu.clientsPurchaseRequests',
        path: '/clientes/requisicoes-compra',
      },
    ],
  },
  {
    kind: 'group',
    labelKey: 'menu.employees',
    icon: PeopleIcon,
    children: [
      { kind: 'leaf', labelKey: 'menu.employeesRegistry', path: '/colaboradores' },
      { kind: 'leaf', labelKey: 'menu.employeesTimesheet', path: '/apontamentos' },
      { kind: 'leaf', labelKey: 'menu.employeesAbsences', path: '/colaboradores/faltas' },
      { kind: 'leaf', labelKey: 'menu.employeesVacations', path: '/colaboradores/ferias' },
      // Taxas removidas do menu (2026-06-07): acesso só via grid de Colaboradores
      // → botão "Gerenciar taxas" no FormDialog do Colaborador (sub-recurso /taxas).
      { kind: 'leaf', labelKey: 'menu.employeesAdvances', path: '/colaboradores/vales' },
      { kind: 'leaf', labelKey: 'menu.employeesInvoices', path: '/colaboradores/notas-fiscais' },
    ],
  },
  {
    kind: 'group',
    labelKey: 'menu.generalRegistries',
    icon: AddIcon,
    children: [
      { kind: 'leaf', labelKey: 'menu.positions', path: '/cadastros-gerais/cargos' },
      { kind: 'leaf', labelKey: 'menu.holidays', path: '/cadastros-gerais/feriados' },
      { kind: 'leaf', labelKey: 'menu.taxes', path: '/cadastros-gerais/impostos' },
      { kind: 'leaf', labelKey: 'menu.hourTypes', path: '/cadastros-gerais/tipos-hora' },
      { kind: 'leaf', labelKey: 'menu.taskTypes', path: '/cadastros-gerais/tipos-tarefa' },
    ],
  },
  {
    kind: 'group',
    labelKey: 'menu.securityAccess',
    icon: LockIcon,
    // GAM Dashboard só entra se a env estiver definida — evita link quebrado em
    // ambientes onde a URL ainda não foi configurada.
    children: [
      { kind: 'leaf', labelKey: 'menu.systemParameters', path: '/parametros' },
      ...(import.meta.env.VITE_GAM_DASHBOARD_URL
        ? [
            {
              kind: 'leaf' as const,
              labelKey: 'menu.gamDashboard',
              externalUrl: import.meta.env.VITE_GAM_DASHBOARD_URL,
            },
          ]
        : []),
    ],
  },
  {
    kind: 'group',
    labelKey: 'menu.reports',
    icon: AssessmentIcon,
    children: [
      { kind: 'leaf', labelKey: 'menu.reportsTimesheet', path: '/relatorios/espelho-ponto' },
      { kind: 'leaf', labelKey: 'menu.reportsBilling', path: '/relatorios/extrato-faturamento' },
      {
        kind: 'leaf',
        labelKey: 'menu.reportsContractorInvoices',
        path: '/relatorios/notas-fiscais-colab',
      },
      { kind: 'leaf', labelKey: 'menu.reportsAdvances', path: '/relatorios/vales' },
    ],
  },
];

export const ExternalIcon = OpenInNewIcon;
